/**
 * 云端菜品库：列表/上传/加载响应的纯函数规范化（与 `/v3/cloud-dish-library` 契约配套）。
 * OAS 中部分 200 schema 为空，此处兼容常见包裹字段，联调后可收紧。
 * 云端菜单详情页：响应与本地 FirstMenu 同构，直接展示，勿再做字段重映射。
 */

import type {
    CloudDishLibraryCreatedCourseDish,
    CloudDishLibraryMenuListSluItem,
} from '../request/api/rbp/cloud-dish-library';

export type { CloudDishLibraryCreatedCourseDish };

/** 云端菜单列表摘要（卡片 / 删除确认 / 复制转移选二级菜单） */
export interface CloudDishLibraryMenuSummary {
    /** 云端菜单 id */
    id: string;
    /** 菜单名称 */
    main_group_name?: string;
    /** 菜品数 */
    dish_count?: number;
    /** 封面图 */
    menu_image?: string;
    /** 来源本地一级菜单 id */
    source_main_group_id?: number;
    /** 创建时间（字段名因后端而异，见 normalize） */
    created_at?: string;
    /** 更新时间 */
    updated_at?: string;
    /** 与本地列表类似的展示日期字段 */
    last_updated_time?: string;
    /**
     * 小类摘要（来自 `GET .../menus` 的 `menu_item_slu`）；供复制/转移跨菜单选目标二级菜单。
     * 无则空数组。
     */
    menu_item_slu?: CloudDishLibraryMenuListSluItem[];
}

/** 上传成功响应（以 OAS example 为准） */
export interface CloudDishLibraryUploadResult {
    /** 本次新建云端菜单份数 */
    uploaded_count?: number;
    /** 本次涉及菜品总数 */
    total_dish_count?: number;
    /** 新建的云端菜单摘要列表 */
    menus?: CloudDishLibraryMenuSummary[];
}

/** 删除成功响应（联调真实体） */
export interface CloudDishLibraryDeleteResult {
    /** 实际删除的云端菜单份数 */
    deleted_menu_count: number;
    /** 实际删除涉及的菜品数 */
    deleted_dish_count: number;
    /** 清理的 OSS 资源数 */
    deleted_oss_count: number;
    /** 请求中未找到、未删除的云端菜单 id */
    not_found_ids: string[];
}

/** add-dishes 成功计数（字段名兼容多种后端命名） */
export interface CloudDishLibraryAddDishesCounts {
    /** 成功添加数量 */
    successCount: number;
    /** 已存在未添加数量 */
    existsCount: number;
    /** 可选：成功添加的菜品名称 */
    names: string[];
    /** 顺带创建的套餐菜品数（接口 `created_course_dish_count`） */
    createdCourseDishCount: number;
    /** 顺带创建的套餐菜品明细（合法项） */
    createdCourseDishes: CloudDishLibraryCreatedCourseDish[];
}

/** 成功弹窗：套餐下新建菜按套餐 → 组归并后的展示结构 */
export interface CreatedCourseDishMealGroup {
    /** 套餐名 */
    setMealName: string;
    /** 该套餐下的分组列表 */
    groups: CreatedCourseDishCourseGroup[];
}

/** 某一套餐组下的新建菜名列表 */
export interface CreatedCourseDishCourseGroup {
    /** 组名（必选组 / 可选组等） */
    courseGroupName: string;
    /** 菜品名（仅 item_name1） */
    dishNames: string[];
}

/** add-dishes 解析失败时的空结果 */
const EMPTY_ADD_DISHES_COUNTS: CloudDishLibraryAddDishesCounts = {
    successCount: 0,
    existsCount: 0,
    names: [],
    createdCourseDishCount: 0,
    createdCourseDishes: [],
};

/**
 * 从未知响应中取出非负整数；非法则返回 fallback。
 * @param value - 原始值
 * @param fallback - 默认值
 */
function toNonNegativeInt(value: unknown, fallback = 0): number {
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n) || n < 0) {
        return fallback;
    }
    return Math.floor(n);
}

/**
 * 规范化列表项内嵌小类摘要；无有效 `_id` 则返回 null。
 * @param item - `menu_item_slu` 数组元素
 */
export function normalizeCloudMenuListSluItem(item: unknown): CloudDishLibraryMenuListSluItem | null {
    if (!item || typeof item !== 'object') {
        return null;
    }
    const row = item as Record<string, unknown>;
    const idRaw = row._id ?? row.id ?? row.cloud_slu_id;
    if (idRaw == null || String(idRaw).trim() === '') {
        return null;
    }
    return {
        _id: String(idRaw),
        dmi_slu_id: toNonNegativeInt(row.dmi_slu_id, 0),
        dmi_slu_name: typeof row.dmi_slu_name === 'string' ? row.dmi_slu_name : '',
        dmi_slu_index: toNonNegativeInt(row.dmi_slu_index, 0),
        not_joining_global_limit: toNonNegativeInt(row.not_joining_global_limit, 0),
        category_order_limit: toNonNegativeInt(row.category_order_limit, 9999),
        table_category_order_limit: toNonNegativeInt(row.table_category_order_limit, 9999),
        dmi_slu_name_i18n: typeof row.dmi_slu_name_i18n === 'string' ? row.dmi_slu_name_i18n : '',
        dish_count: toNonNegativeInt(row.dish_count, 0),
    };
}

/**
 * 规范化 `menu_item_slu` 数组；非法项丢弃。
 * @param raw - 原始小类列表
 */
function normalizeCloudMenuListSluItems(raw: unknown): CloudDishLibraryMenuListSluItem[] {
    if (!Array.isArray(raw)) {
        return [];
    }
    const out: CloudDishLibraryMenuListSluItem[] = [];
    for (const item of raw) {
        const n = normalizeCloudMenuListSluItem(item);
        if (n) {
            out.push(n);
        }
    }
    return out;
}

/**
 * 将单条未知对象规范为云端菜单摘要；无有效 id 则返回 null。
 * @param item - 列表或上传 menus 中的一项
 */
export function normalizeCloudMenuSummary(item: unknown): CloudDishLibraryMenuSummary | null {
    if (!item || typeof item !== 'object') {
        return null;
    }
    const row = item as Record<string, unknown>;
    // id 可能为 string / ObjectId 字符串
    const idRaw = row.id ?? row._id ?? row.cloud_menu_id;
    if (idRaw == null || String(idRaw).trim() === '') {
        return null;
    }
    const dishRaw = row.dish_count ?? row.dishCount ?? row.total_dish_count;
    const menu_item_slu = normalizeCloudMenuListSluItems(row.menu_item_slu);
    return {
        id: String(idRaw),
        main_group_name:
            typeof row.main_group_name === 'string'
                ? row.main_group_name
                : typeof row.name === 'string'
                  ? row.name
                  : undefined,
        dish_count: dishRaw == null ? undefined : toNonNegativeInt(dishRaw, 0),
        menu_image: typeof row.menu_image === 'string' ? row.menu_image : undefined,
        source_main_group_id:
            row.source_main_group_id == null ? undefined : toNonNegativeInt(row.source_main_group_id),
        created_at: typeof row.created_at === 'string' ? row.created_at : undefined,
        updated_at: typeof row.updated_at === 'string' ? row.updated_at : undefined,
        last_updated_time: typeof row.last_updated_time === 'string' ? row.last_updated_time : undefined,
        menu_item_slu: menu_item_slu.length > 0 ? menu_item_slu : undefined,
    };
}

/**
 * 规范化云端菜单列表响应（数组或 `{ menus|data|list }` 包裹）。
 * @param res - GET .../menus 原始响应
 */
export function normalizeCloudMenuListResponse(res: unknown): CloudDishLibraryMenuSummary[] {
    if (res == null) {
        return [];
    }
    // 业务 error 对象不解析为列表
    if (typeof res === 'object' && !Array.isArray(res) && (res as { error?: boolean }).error) {
        return [];
    }
    let rawList: unknown[] = [];
    if (Array.isArray(res)) {
        rawList = res;
    } else if (typeof res === 'object') {
        const o = res as Record<string, unknown>;
        if (Array.isArray(o.menus)) {
            rawList = o.menus;
        } else if (Array.isArray(o.data)) {
            rawList = o.data;
        } else if (Array.isArray(o.list)) {
            rawList = o.list;
        }
    }
    const out: CloudDishLibraryMenuSummary[] = [];
    for (const item of rawList) {
        const n = normalizeCloudMenuSummary(item);
        if (n) {
            out.push(n);
        }
    }
    return out;
}

/**
 * 规范化上传响应（透传已知字段 + menus 摘要）。
 * @param res - POST .../upload 原始响应
 */
export function normalizeCloudUploadResult(res: unknown): CloudDishLibraryUploadResult | null {
    if (!res || typeof res !== 'object' || (res as { error?: boolean }).error) {
        return null;
    }
    const o = res as Record<string, unknown>;
    const menus = Array.isArray(o.menus)
        ? o.menus.map(normalizeCloudMenuSummary).filter((m): m is CloudDishLibraryMenuSummary => m != null)
        : undefined;
    return {
        uploaded_count: o.uploaded_count == null ? undefined : toNonNegativeInt(o.uploaded_count),
        total_dish_count: o.total_dish_count == null ? undefined : toNonNegativeInt(o.total_dish_count),
        menus,
    };
}

/**
 * 规范化删除云端菜单响应。
 * @param res - POST .../menus/delete 原始响应
 */
export function normalizeCloudDeleteResult(res: unknown): CloudDishLibraryDeleteResult | null {
    if (!res || typeof res !== 'object' || (res as { error?: boolean }).error) {
        return null;
    }
    const o = res as Record<string, unknown>;
    const notFoundRaw = o.not_found_ids;
    const not_found_ids = Array.isArray(notFoundRaw)
        ? notFoundRaw.map((id) => String(id)).filter((id) => id.trim() !== '')
        : [];
    return {
        deleted_menu_count: toNonNegativeInt(o.deleted_menu_count, 0),
        deleted_dish_count: toNonNegativeInt(o.deleted_dish_count, 0),
        deleted_oss_count: toNonNegativeInt(o.deleted_oss_count, 0),
        not_found_ids,
    };
}

/**
 * 规范化单条 `created_course_dishes` 元素；缺套餐名或菜名则丢弃。
 * @param item - 原始元素
 */
function normalizeCreatedCourseDish(item: unknown): CloudDishLibraryCreatedCourseDish | null {
    if (!item || typeof item !== 'object') {
        return null;
    }
    const row = item as Record<string, unknown>;
    const set_meal_name = typeof row.set_meal_name === 'string' ? row.set_meal_name.trim() : '';
    const item_name1 = typeof row.item_name1 === 'string' ? row.item_name1.trim() : '';
    if (!set_meal_name || !item_name1) {
        return null;
    }
    return {
        set_meal_name,
        course_group_name:
            typeof row.course_group_name === 'string' ? row.course_group_name.trim() : '',
        item_name1,
        item_name2: typeof row.item_name2 === 'string' ? row.item_name2 : '',
    };
}

/**
 * 将套餐新建菜明细按套餐名 → 组名归并，保持首次出现顺序（成功弹窗第二块列表）。
 * @param items - 已规范化的明细
 */
export function groupCreatedCourseDishes(
    items: CloudDishLibraryCreatedCourseDish[],
): CreatedCourseDishMealGroup[] {
    // 套餐名 → 索引，保证顺序稳定
    const mealOrder: string[] = [];
    const mealMap = new Map<string, Map<string, string[]>>();
    for (const item of items) {
        let groupMap = mealMap.get(item.set_meal_name);
        if (!groupMap) {
            groupMap = new Map();
            mealMap.set(item.set_meal_name, groupMap);
            mealOrder.push(item.set_meal_name);
        }
        const groupKey = item.course_group_name;
        let names = groupMap.get(groupKey);
        if (!names) {
            names = [];
            groupMap.set(groupKey, names);
        }
        names.push(item.item_name1);
    }
    return mealOrder.map((setMealName) => {
        const groupMap = mealMap.get(setMealName)!;
        const groups: CreatedCourseDishCourseGroup[] = [];
        groupMap.forEach((dishNames, courseGroupName) => {
            groups.push({ courseGroupName, dishNames });
        });
        return { setMealName, groups };
    });
}

/**
 * 从 add-dishes 响应解析成功数 / 已存在数 / 名称列表 / 套餐新建菜（兼容多种字段名）。
 * @param res - POST .../add-dishes 原始响应
 */
export function pickAddDishesResultCounts(res: unknown): CloudDishLibraryAddDishesCounts {
    if (!res || typeof res !== 'object' || (res as { error?: boolean }).error) {
        return { ...EMPTY_ADD_DISHES_COUNTS };
    }
    const o = res as Record<string, unknown>;
    // 成功数：多种后端命名
    const successCount = toNonNegativeInt(
        o.success_count ?? o.added_count ?? o.created_count ?? o.successCount ?? o.addedCount,
        0,
    );
    // 已存在 / 跳过重复数（联调真实体为 skipped_duplicate_count）
    const existsCount = toNonNegativeInt(
        o.exists_count ??
            o.existed_count ??
            o.already_exists_count ??
            o.skipped_duplicate_count ??
            o.duplicate_count ??
            o.existsCount ??
            o.existedCount ??
            o.skippedDuplicateCount,
        0,
    );
    // 名称列表
    let names: string[] = [];
    const nameRaw =
        o.added_names ?? o.success_names ?? o.dish_names ?? o.names ?? o.added_dish_names;
    if (Array.isArray(nameRaw)) {
        names = nameRaw
            .map((n) => {
                if (typeof n === 'string') {
                    return n;
                }
                if (n && typeof n === 'object') {
                    const row = n as Record<string, unknown>;
                    // 与确认列表一致：item_name1 → item_name2 → name / id
                    const n1 =
                        typeof row.item_name1 === 'string'
                            ? row.item_name1
                            : typeof row.name1 === 'string'
                              ? row.name1
                              : undefined;
                    const n2 =
                        typeof row.item_name2 === 'string'
                            ? row.item_name2
                            : typeof row.name2 === 'string'
                              ? row.name2
                              : undefined;
                    const idFallback =
                        row.id != null
                            ? String(row.id)
                            : typeof row.name === 'string'
                              ? row.name
                              : typeof row.item_name === 'string'
                                ? row.item_name
                                : '';
                    return resolveCloudDishDisplayName(n1, n2, idFallback);
                }
                return '';
            })
            .filter((s) => s.trim() !== '');
    }
    // 套餐下顺带新建的菜
    const createdCourseDishCount = toNonNegativeInt(o.created_course_dish_count, 0);
    const createdCourseDishes: CloudDishLibraryCreatedCourseDish[] = [];
    if (Array.isArray(o.created_course_dishes)) {
        for (const raw of o.created_course_dishes) {
            const n = normalizeCreatedCourseDish(raw);
            if (n) {
                createdCourseDishes.push(n);
            }
        }
    }
    return {
        successCount,
        existsCount,
        names,
        createdCourseDishCount,
        createdCourseDishes,
    };
}

/**
 * 云端卡片副文案：优先 last_updated_time / updated_at / created_at；空则「—」。
 * @param menu - 云端菜单摘要
 */
export function formatCloudMenuCardSubtitle(menu: CloudDishLibraryMenuSummary): string {
    const raw = menu.last_updated_time || menu.updated_at || menu.created_at || '';
    if (!raw.trim()) {
        return '—';
    }
    // 已是 DD/MM/YYYY 则直接用；ISO 则转本地日期展示（与列表习惯接近）
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw.trim())) {
        return raw.trim();
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) {
        return raw.trim();
    }
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
}

/**
 * 汇总已选云端菜单的菜品总数（删除确认用）。
 * @param menus - 全量云端列表
 * @param selectedIds - 已选云端菜单 id
 */
export function sumSelectedCloudDishCount(
    menus: CloudDishLibraryMenuSummary[],
    selectedIds: string[],
): number {
    if (!selectedIds.length) {
        return 0;
    }
    const set = new Set(selectedIds);
    let total = 0;
    for (const m of menus) {
        if (set.has(m.id)) {
            total += m.dish_count ?? 0;
        }
    }
    return total;
}

/** 云端详情中的小类摘要（加载弹窗左侧） */
export interface CloudDishLibraryCategorySummary {
    /** 小类 id（字符串化） */
    id: string;
    /** 小类名称 */
    name: string;
}

/** 云端详情中的菜品行（加载弹窗表格） */
export interface CloudDishLibraryDishRow {
    /** 云端菜品 id */
    id: string;
    /**
     * 列表/确认浮层展示名：优先 `item_name1`，其次 `item_name2`，再无则 id。
     */
    name: string;
    /** 名称1（接口 `item_name1`） */
    name1?: string;
    /** 名称2（接口 `item_name2`） */
    name2?: string;
    /** 价格展示 */
    price?: string | number;
    /** 编码 */
    code?: string;
    /** 菜品图 URL（来自详情 `dish_image`） */
    image?: string;
    /** 所属小类 id */
    categoryId?: string;
}

/**
 * 云端菜品展示名：item_name1 → item_name2 → id。
 * @param name1 - 菜品名称1（可空）
 * @param name2 - 菜品名称2（可空）
 * @param id - 云端菜品 id（兜底）
 */
export function resolveCloudDishDisplayName(
    name1: string | undefined,
    name2: string | undefined,
    id: string,
): string {
    const n1 = typeof name1 === 'string' ? name1.trim() : '';
    if (n1) {
        return n1;
    }
    const n2 = typeof name2 === 'string' ? name2.trim() : '';
    if (n2) {
        return n2;
    }
    return id;
}

/**
 * 从详情响应提取小类列表（兼容多种字段名）。
 * @param detail - GET 详情原始响应
 */
export function extractCloudDetailCategories(detail: unknown): CloudDishLibraryCategorySummary[] {
    if (!detail || typeof detail !== 'object') {
        return [];
    }
    const o = detail as Record<string, unknown>;
    const raw =
        o.categories ??
        o.slus ??
        o.menu_item_slu ??
        o.second_menus ??
        (o.menu && typeof o.menu === 'object'
            ? (o.menu as Record<string, unknown>).menu_item_slu
            : undefined);
    if (!Array.isArray(raw)) {
        return [];
    }
    const out: CloudDishLibraryCategorySummary[] = [];
    for (const item of raw) {
        if (!item || typeof item !== 'object') {
            continue;
        }
        const row = item as Record<string, unknown>;
        const idRaw = row.id ?? row._id ?? row.dmi_slu_id ?? row.slu_id ?? row.category_id;
        if (idRaw == null || String(idRaw).trim() === '') {
            continue;
        }
        const name =
            (typeof row.name === 'string' && row.name) ||
            (typeof row.dmi_slu_name === 'string' && row.dmi_slu_name) ||
            (typeof row.main_group_name === 'string' && row.main_group_name) ||
            String(idRaw);
        out.push({ id: String(idRaw), name });
    }
    return out;
}

/**
 * 从详情响应提取菜品行（可选按小类过滤）。
 * @param detail - GET 详情原始响应
 * @param categoryId - 当前小类；null 表示不过滤小类
 */
export function extractCloudDetailDishes(
    detail: unknown,
    categoryId: string | null,
): CloudDishLibraryDishRow[] {
    if (!detail || typeof detail !== 'object') {
        return [];
    }
    const o = detail as Record<string, unknown>;
    const out: CloudDishLibraryDishRow[] = [];

    /**
     * 将单条菜品对象推入 out。
     * @param item - 菜品原始对象
     * @param catId - 所属小类
     */
    const pushDish = (item: unknown, catId?: string) => {
        if (!item || typeof item !== 'object') {
            return;
        }
        const row = item as Record<string, unknown>;
        const idRaw = row.id ?? row._id ?? row.cloud_dish_id ?? row.item_id ?? row.menu_item_id;
        if (idRaw == null || String(idRaw).trim() === '') {
            return;
        }
        // 名称1 / 名称2：优先接口 item_name*，兼容 name*
        const name1 =
            typeof row.item_name1 === 'string'
                ? row.item_name1
                : typeof row.name1 === 'string'
                  ? row.name1
                  : undefined;
        const name2 =
            typeof row.item_name2 === 'string'
                ? row.item_name2
                : typeof row.name2 === 'string'
                  ? row.name2
                  : undefined;
        const id = String(idRaw);
        out.push({
            id,
            name: resolveCloudDishDisplayName(name1, name2, id),
            name1,
            name2,
            price: (row.price ??
                row.price_1 ??
                row.normal_price ??
                row.item_price) as string | number | undefined,
            code: typeof row.code === 'string' ? row.code : typeof row.item_code === 'string' ? row.item_code : undefined,
            /** 优先 `dish_image`（云端详情菜品图字段），兼容旧别名 */
            image:
                typeof row.dish_image === 'string' && row.dish_image.trim()
                    ? row.dish_image.trim()
                    : typeof row.image === 'string' && row.image.trim()
                      ? row.image.trim()
                      : typeof row.foodImage === 'string' && row.foodImage.trim()
                        ? row.foodImage.trim()
                        : typeof row.bigFoodImage === 'string' && row.bigFoodImage.trim()
                          ? row.bigFoodImage.trim()
                          : undefined,
            categoryId: catId,
        });
    };

    // 扁平 dishes
    const flat = o.dishes ?? o.menu_items ?? o.items;
    if (Array.isArray(flat)) {
        for (const d of flat) {
            const row = d && typeof d === 'object' ? (d as Record<string, unknown>) : null;
            const cat =
                row && (row.category_id ?? row.dmi_slu_id ?? row.slu_id) != null
                    ? String(row.category_id ?? row.dmi_slu_id ?? row.slu_id)
                    : undefined;
            if (categoryId && cat && cat !== categoryId) {
                continue;
            }
            if (categoryId && !cat) {
                // 无小类信息时，有 category 过滤则仍展示（避免空表）
            }
            pushDish(d, cat);
        }
        if (out.length > 0) {
            return out;
        }
    }

    // 按小类嵌套：menu_item_slu[].menu_item
    const slus =
        o.menu_item_slu ??
        o.categories ??
        o.slus ??
        (o.menu && typeof o.menu === 'object'
            ? (o.menu as Record<string, unknown>).menu_item_slu
            : undefined);
    if (Array.isArray(slus)) {
        for (const slu of slus) {
            if (!slu || typeof slu !== 'object') {
                continue;
            }
            const s = slu as Record<string, unknown>;
            const sid = s.id ?? s._id ?? s.dmi_slu_id ?? s.slu_id ?? s.category_id;
            const catId = sid != null ? String(sid) : undefined;
            if (categoryId && catId && catId !== categoryId) {
                continue;
            }
            const dishes = s.menu_item ?? s.dishes ?? s.items ?? s.menu_items;
            if (Array.isArray(dishes)) {
                for (const d of dishes) {
                    pushDish(d, catId);
                }
            }
        }
    }
    return out;
}

/**
 * 按关键字过滤云端菜品（仅匹配 item_name1 / item_name2，不区分大小写；前端本地过滤）。
 * @param dishes - 菜品行
 * @param keyword - 搜索词
 */
export function filterCloudDishesByKeyword(
    dishes: CloudDishLibraryDishRow[],
    keyword: string,
): CloudDishLibraryDishRow[] {
    const q = keyword.trim().toLowerCase();
    if (!q) {
        return dishes;
    }
    return dishes.filter((d) => {
        // 仅搜「菜品名称1」「菜品名称2」（对应接口 item_name1 / item_name2），按字段独立匹配
        return [d.name1, d.name2].some((n) => typeof n === 'string' && n.toLowerCase().includes(q));
    });
}

